import { useCallback, useEffect, useMemo } from "react";

import { useLocalStorageState } from "@/hooks/useLocalStorageState";
import { PROJECT_FAVORITES_STORAGE_PREFIX } from "@/lib/local-storage-keys";
import { rememberPreservedStorageKey } from "@/lib/logout-storage";

export function useProjectFavorites(orgId: string): {
  favoriteIds: string[];
  favoriteSet: Set<string>;
  isFavorite: (projectId: string) => boolean;
  toggleFavorite: (projectId: string) => void;
} {
  const [favoriteIds, setFavoriteIds] = useLocalStorageState<string[]>(
    `${PROJECT_FAVORITES_STORAGE_PREFIX}${orgId}`,
    [],
  );

  const favoriteSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);

  useEffect(() => {
    rememberPreservedStorageKey(
      `${PROJECT_FAVORITES_STORAGE_PREFIX}${orgId}`,
      JSON.stringify(favoriteIds),
    );
  }, [favoriteIds, orgId]);

  const isFavorite = useCallback(
    (projectId: string) => favoriteSet.has(projectId),
    [favoriteSet],
  );

  const toggleFavorite = useCallback(
    (projectId: string) => {
      setFavoriteIds((prev) =>
        prev.includes(projectId)
          ? prev.filter((id) => id !== projectId)
          : [...prev, projectId],
      );
    },
    [setFavoriteIds],
  );

  return { favoriteIds, favoriteSet, isFavorite, toggleFavorite };
}
